import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import translate from '@translate';
import Typography from '../../components/Typography';
import Select, { OptionType } from '../../components/Select';
import Button from '../../components/Button';
import NewProductBlock from '../OrderDetailContainer/NewProductBlock';
import ProductsTable from '../OrderDetailContainer/ProductsTable';
import { OrderProductType } from '../../types/order';
import ProductsSelect, { ProductOptionType } from '../../components/ProductsSelect';
import TableSimple from '../../components/TableSimple';
import { TableColumnType, TableRowType } from '../../types/table';
import { sizes } from '../../themes/default';
import Grid, { GridItem } from '../../components/Grid';
import api from '@api';
import { ORDER_GRID_PRODUCTS_URL } from '../../resources/ordersGrid/sagas';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Checkbox from '../../components/Checkbox';
import { InfoIcon } from '../../components/SkladonIcon/Icons';
import Tooltip from '../../components/Tooltip';
import { styled } from '@mui/material/styles';
import { RootStoreType } from '../../types/store';
import { useSelector } from 'react-redux';
import { FormErrorsType } from '../../types/global';

export interface AddRemoveProductDialogPropsType {
    title: string | ReactNode;
    content: string | ReactNode;
    onClose: () => void;
    addAction: (data: OrderProductType[], addProductsIfExists: boolean) => void;
    removeAction: (data: number[]) => void;
    onCancel?: () => void;
    disabled?: boolean;
    orderIds: number[];
}

const allColumns: TableColumnType[] = [
    {
        key: 'SKU',
        label: 'SKU',
        defaultShow: true
    },
    {
        key: 'NAME',
        label: 'name',
        defaultShow: true
    },
    {
        key: 'EAN',
        label: 'EAN',
        defaultShow: true
    }
];

const InfoIconStyled = styled('span')({
    marginLeft: '10px',

    '& svg': {
        width: '17px',
        height: '17px',
        marginRight: '10px'
    }
});

const getProductsForRemoval = async (orderIds: number[]): Promise<ProductOptionType[]> => {
    const products = await api('POST', ORDER_GRID_PRODUCTS_URL, {
        columns: ['PRODUCT_ID', 'SKU', 'NAME', 'EAN'],
        filters: [
            [
                {
                    column: 'ID',
                    value: orderIds,
                    operator: 'IN'
                }
            ]
        ]
    });

    const uniqueProducts = Array.from(new Set(products.data.data.data.map((product: any) => product.PRODUCT_ID)))
        .map(id => products.data.data.data.find((product: any) => product.PRODUCT_ID === id));

    return uniqueProducts.map((product) => ({
        label: product.EAN ? `${product.SKU} | ${product.NAME} | ${product.EAN}` : `${product.SKU} | ${product.NAME}`,
        value: product.PRODUCT_ID
    }));
};

const ADD = 'add';
const REMOVE = 'remove';
const AddRemoveProductDialog: FC<AddRemoveProductDialogPropsType> = (props) => {
    const [data, setData] = useState<any>({});
    const [addProducts, setAddProducts] = useState<OrderProductType[]>([]);
    const [removeProductsIds, setRemoveProductsIds] = useState<number[]>([]);
    const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [mode, setMode] = useState<'add' | 'remove' | null>(null);
    const [selectedMode, setSelectedMode] = useState<'add' | 'remove' | null>(null);
    const [removalOptions, setRemovalOptions] = useState<ProductOptionType[]>([]);
    const [checkboxValue, setCheckboxValue] = useState<boolean>(false); // Přidání stavu pro Checkbox
    const formRef = useRef<HTMLFormElement>(null);
    const orderProductCustomsTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.orderProductCustomsTypeCodes
    );
    const options: OptionType[] = [
        { value: ADD, label: translate('addRemoveProductDialog.addProducts') },
        { value: REMOVE, label: translate('addRemoveProductDialog.removeProducts') }
    ];

    useEffect(() => {
        if (mode === REMOVE && removalOptions.length === 0) {
            getProductsForRemoval(props.orderIds).then(setRemovalOptions);
        }
    }, [mode]);

    const onChangeMode = (value: any, error: any) => {
        setSelectedMode(value);
    };

    const onChangeProducts = (value: OrderProductType[]) => {
        setAddProducts(value);
    };

    const handleAddToRemove = (value: any) => {
        if (selectedProductId) {
            setRemoveProductsIds(Array.from(new Set([...removeProductsIds, selectedProductId])));
        }
    };

    const handleCheckboxChange = (value: boolean | number) => {
        setCheckboxValue(!!value);
    };

    let content: ReactNode | string = '';

    function handleRemoveRow(row: TableRowType) {
        setRemoveProductsIds(removeProductsIds.filter(id => id !== row.ID));
    }

    if (!mode) {
        content = (
            <>
                <Typography component="div">{props.content}</Typography>
                <Select
                    options={options}
                    value={selectedMode}
                    onChange={onChangeMode}
                    fullWidth
                    required
                />
            </>
        );
    } else if (mode === ADD) {
        content = <>
            <NewProductBlock onChange={onChangeProducts} orderProductCustomsTypeCodes={orderProductCustomsTypeCodes} editErrors={editErrors}
                             data={addProducts} />
            <label style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox value={checkboxValue} onChange={handleCheckboxChange} />
                <span>{translate('addRemoveProductDialog.addProductIfExists')}</span>
            </label>
            <ProductsTable
                newOrder={true}
                allowOrderProductDetail={false}
                onChange={onChangeProducts}
                onChangeProductsForSets={() => undefined}
                orderProductCustomsTypeCodes={orderProductCustomsTypeCodes}
                orderProductCustomizationTypeCodes={[]}
                data={addProducts}
                productsForSets={[]}
                orderData={{}}
                setOpenProductDetailId={() => undefined}
                editErrors={editErrors}
            />
        </>;
    } else if (mode === REMOVE) {
        content = <>
            <>
                <Typography gutterBottom variant="h3">
                    {translate('orderDetail.removeProduct')}
                    <Tooltip title={translate('addRemoveProductDialog.removeOrders.info')}>
                        <InfoIconStyled>
                            <InfoIcon />
                        </InfoIconStyled>
                    </Tooltip>
                </Typography>
                <form ref={formRef}>
                    <Grid>
                        <GridItem cols={10}>
                            <ProductsSelect
                                idKey="ID"
                                options={removalOptions}
                                onChange={(value: any, error) => setSelectedProductId(value)}
                                name="PRODUCT_ID"
                                label={translate('orderDetail.productTable.name')}
                                fullWidth
                                required
                            />
                        </GridItem>
                        <GridItem cols={2}>
                            <Button
                                onClick={handleAddToRemove}
                                sx={{ marginTop: sizes.input_top_PD }}
                                mobileFullWidht
                            >
                                + {translate('orderDetail.addToRemove')}
                            </Button>
                        </GridItem>
                    </Grid>
                </form>
            </>
            <TableSimple
                rowIdKey="ID"
                rows={removalOptions.filter(product => removeProductsIds.includes(Number(product.value))).map(product => ({
                    ID: product.value,
                    SKU: product.label.split(' | ')[0],
                    NAME: product.label.split(' | ')[1],
                    EAN: product.label.split(' | ')[2] || null
                }))}
                columsLabelPrefix="productDetail.productRelationstable"
                allColumns={allColumns}
                initSort={{ column: 'ID', direction: 'ASC', createIdNumber: true }}
                rowActions={[
                    {
                        icon: <HighlightOffIcon />,
                        label: translate('productDetail.productRelationstable.remove'),
                        onClick: handleRemoveRow
                    }
                ]}
                // disableFilter
                maxHeight={'unset'}
            />
        </>;
    }

    const handleOk = () => {
        if (!mode && selectedMode) {
            setMode(selectedMode);
        } else if (mode === ADD && addProducts.length > 0) {
            props.addAction(addProducts.map((product) => ({
                ...product,
                ORDERS_PRODUCTS_ID: undefined
            })), checkboxValue);
            props.onClose();
        } else if (mode === REMOVE && removeProductsIds.length > 0) {
            props.removeAction(removeProductsIds);
            props.onClose();
        }
    };

    const handleCancel: any = () => {
        if (props.onCancel) {
            props.onCancel();
        }
        props.onClose();
    };

    return (
        <Dialog maxWidth={!mode ? 'xs' : 'lg'} open fullWidth>
            <DialogTitle id="confirmation-dialog-title">{props.title}</DialogTitle>
            <DialogContent dividers>
                {content}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={handleCancel}>
                    {translate('confirmDialog.cancel')}
                </Button>
                <Button variant="contained" onClick={handleOk} disabled={props.disabled}>
                    {!mode ? translate('confirmDialog.ok') : translate('massEditProductDialog.ok')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddRemoveProductDialog;
